import autoTable from "jspdf-autotable";

import {
  PDF_TABLE_STYLE,
  PDFCOLORSHEME,
} from "../../../../../component/constent";

/* =========================================================
   FORMAT NUMBER
========================================================= */
const format = (value) => {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/* =========================================================
   MONTH NAME
========================================================= */
const monthName = (month) => {
  const months = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  const value = Number(month);

  return value >= 1 && value <= 12
    ? months[value - 1]
    : String(month || "");
};

/* =========================================================
   GET PERIOD FROM ROWS
   Example:
   JAN-2026 to MAR - 2026
========================================================= */
const getPeriod = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return "";

  const row = rows.find(
    (item) => item?.Quarter && item?.Year
  );

  if (!row) return "";

  const quarter = Number(row.Quarter);
  const year = Number(row.Year);

  const quarterMonths = {
    1: [1, 3],
    2: [4, 6],
    3: [7, 9],
    4: [10, 12],
  };

  const months = quarterMonths[quarter];

  if (!months || !year) return "";

  const startMonth = monthName(months[0]);
  const endMonth = monthName(months[1]);

  const shortYear = String(year).slice(-2);

  return `${startMonth}${shortYear} to ${endMonth}${shortYear}`;
};

/* =========================================================
   ACTIVITY GROUP
========================================================= */
const getActivityGroup = (row) => {
  /*
    Supports:
      ActivityGroup
      ActivityType
      Activity

    If your API already returns PCV / OCV / LHB,
    it will directly use that value.
  */

  const value =
    row?.ActivityGroup ??
    row?.ActivityType ??
    row?.Activity ??
    "";

  const text = String(value).trim().toUpperCase();

  // If API returns actual group name
  if (["PCV", "OCV", "LHB"].includes(text)) {
    return text;
  }

  // If API returns numeric group
  const numeric = Number(value);

  if (numeric === 1) return "PCV";
  if (numeric === 2) return "OCV";
  if (numeric === 3) return "LHB";

  return text;
};

/* =========================================================
   DRAW FINANCIAL TABLE
========================================================= */
export function drawFinancialTable(doc, rows = []) {
  if (!doc) return;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  /* =======================================================
     PERIOD
  ======================================================= */

  const period = getPeriod(rows);

  /* =======================================================
     TITLE
  ======================================================= */

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);

  const title =
    "Quarterly Statement of POH Unit cost of Mechanical Workshop Gorakhpur";

  doc.text(title, pageWidth / 2, 13, {
    align: "center",
  });

  /* =======================================================
     DYNAMIC PERIOD
  ======================================================= */

  doc.setFontSize(11);

  doc.text(
    period
      ? `for ${period}`
      : "",
    pageWidth / 2,
    19,
    {
      align: "center",
    }
  );

  /* =======================================================
     TABLE START
  ======================================================= */

  let startY = 25;

  if (doc.lastAutoTable) {
    startY = doc.lastAutoTable.finalY + 8;
  }

  /* =======================================================
     BUILD TABLE BODY
  ======================================================= */

  const body = [];

  let currentGroup = "";

  rows.forEach((row) => {
    const group = getActivityGroup(row);

    /*
      Add group heading only when group changes.
      Example:

      PCV
      POH Normal Non AC
      POH Normal AC
      ...

      OCV
      POH Normal Non AC
      ...
    */
    if (group && group !== currentGroup) {
      body.push([
        {
          content: group,
          colSpan: 7,
          styles: {
            fontStyle: "bold",
            halign: "left",
            valign: "middle",
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            lineWidth: 0.4,
          },
        },
      ]);

      currentGroup = group;
    }

    body.push([
      String(
        row?.POHFullDescription ??
        row?.Description ??
        row?.ActivityDescription ??
        ""
      ).trim(),

      format(Number(row?.Expr1 || 0) * Number(row?.OutTern || 0)),
      format(Number(row?.Expr2 || 0) * Number(row?.OutTern || 0)),
      format(Number(row?.Expr3 || 0) * Number(row?.OutTern || 0)),
      format(Number(row?.Expr4 || 0) * Number(row?.OutTern || 0)),
      format(Number(row?.Total || 0) * Number(row?.OutTern || 0)),
      format(row?.OutTern),
    ]);
  });

  /* =======================================================
     TABLE
  ======================================================= */

  autoTable(doc, {
    startY,

    ...PDF_TABLE_STYLE,

    theme: "grid",

    head: [
      [
        {
          content: "Activity",
          rowSpan: 2,
        },
        {
          content: "Labour Per\nVechicle",
          rowSpan: 2,
        },
        {
          content: "Material Per\nVechicle",
          rowSpan: 2,
        },
        {
          content: "On Cost",
          colSpan: 2,
        },
        {
          content: "Unit Cost\nper\nVechicle\n{ 000 }",
          rowSpan: 2,
        },
        {
          content: "Total Out Tern",
          rowSpan: 2,
        },
      ],
      [
        "Labour Per\nVechicle",
        "Material Per\nVechicle",
      ],
    ],

    body,

    /* =====================================================
       GENERAL STYLE
    ===================================================== */

    styles: {
      font: "helvetica",
      fontSize: 8.5,

      cellPadding: {
        top: 3,
        bottom: 3,
        left: 2,
        right: 2,
      },

      lineWidth: 0.4,
      lineColor: [0, 0, 0],

      textColor: [0, 0, 0],

      valign: "middle",

      halign: "right",
    },

    /* =====================================================
       HEADER STYLE
    ===================================================== */

    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],

      fontStyle: "bold",

      fontSize: 8.5,

      halign: "center",
      valign: "middle",

      lineWidth: 0.5,
      lineColor: [0, 0, 0],

      cellPadding: {
        top: 3,
        bottom: 3,
        left: 2,
        right: 2,
      },
    },

    /* =====================================================
       COLUMN WIDTH
    ===================================================== */

    columnStyles: {
      0: {
        cellWidth: 40,
        halign: "left",
      },

      1: {
        cellWidth: 23,
        halign: "right",
      },

      2: {
        cellWidth: 23,
        halign: "right",
      },

      3: {
        cellWidth: 23,
        halign: "right",
      },

      4: {
        cellWidth: 23,
        halign: "right",
      },

      5: {
        cellWidth: 25,
        halign: "right",
      },

      6: {
        cellWidth: 25,
        halign: "right",
      },
    },

    margin: {
      left: 11,
      right: 11,
    },

    /* =====================================================
       GROUP ROW + DATA ROW
    ===================================================== */

    didParseCell: (data) => {
      /*
        Group heading rows contain an object in column 0
        and span all 7 columns.
      */

      if (
        data.section === "body" &&
        data.row.raw?.[0]?.colSpan === 7
      ) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.halign = "left";
        data.cell.styles.fontSize = 9;

        data.cell.styles.cellPadding = {
          top: 2,
          bottom: 2,
          left: 3,
          right: 2,
        };
      }
    },

    /* =====================================================
       FOOTER
    ===================================================== */

    // didDrawPage: (data) => {
    //   const pageNumber = doc.internal.getNumberOfPages();

    //   doc.setFont("helvetica", "normal");
    //   doc.setFontSize(8);

    //   doc.text(
    //     `Mechanical Workshop Gorakhpur, NER`,
    //     pageWidth / 2,
    //     pageHeight - 5,
    //     {
    //       align: "center",
    //     }
    //   );

    //   doc.text(
    //     `Page ${pageNumber}`,
    //     pageWidth - 12,
    //     pageHeight - 5,
    //     {
    //       align: "right",
    //     }
    //   );
    // },
  });

  /* =======================================================
     AFTER TABLE
  ======================================================= */

  let y = doc.lastAutoTable.finalY + 10;

  /* =======================================================
     FORWARDING NOTE
  ======================================================= */

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(0, 0, 0);

  doc.text(
    "Forwarded to Dy FA& CAO/S&W for information.",
    14,
    y
  );

  /* =======================================================
     SIGNATURE SECTION
  ======================================================= */

  y += 25;

  /*
     Left Signature
  */

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  doc.text(
    "CWM",
    14,
    y
  );

  /*
     Right Signature
  */

  doc.text(
    "Sr AFA /W",
    pageWidth - 35,
    y,
    {
      align: "center",
    }
  );

  /* =======================================================
     OPTIONAL SIGNATURE LINE
  ======================================================= */

  /*
  Uncomment if you want actual signature lines.

  doc.setLineWidth(0.3);

  doc.line(
    14,
    y - 3,
    45,
    y - 3
  );

  doc.line(
    pageWidth - 50,
    y - 3,
    pageWidth - 20,
    y - 3
  );
  */

  return y;
}